import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { useIntakeSession, useRetryIntakeInterpretation } from '../hooks/useIntake';
import { useZonderGekozenKlant } from '../hooks/useZonderGekozenKlant';
import { KiesEersteKlant } from '../components/KiesEersteKlant';
import { SYNC_ROW_THRESHOLD } from '../intake.constants';
import { IntakeReviewPage } from './IntakeReviewPage';
import type { SubmitProbleem } from '../submit-foutmelding';

/**
 * Hosts /intake/:sessionId across the whole intake lifecycle: upload/interpreting progress
 * (h5-3), the AD-7 "interpretation interrupted" retry action (h5-3), the full review screen
 * (h5-5, `IntakeReviewPage`) once interpretation completes, and a simple confirmation once the
 * session has been submitted.
 */
export function IntakeSessionDetailPage() {
  const { t, i18n } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { data: session, isLoading, isError, error, refetch } = useIntakeSession(sessionId ?? null);
  const retryMutation = useRetryIntakeInterpretation();
  // h7-4: artikelen die bij de zojuist geslaagde indiening niet gepubliceerd konden worden. Ze
  // horen bij dit scherm en niet bij de sessie — ze staan nergens opgeslagen, dus na herladen
  // belooft de melding ze ook niet meer. Opnieuw indienen zou de gegevens dubbel opslaan.
  const [publicatieWaarschuwingen, setPublicatieWaarschuwingen] = useState<SubmitProbleem[]>([]);
  // spec-klantsimulatie-aanmeldingen: een interne rol zonder gekozen klant ziet geen aanmelding.
  const zonderKlant = useZonderGekozenKlant();
  if (zonderKlant) return <KiesEersteKlant />;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="intake-detail-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--border)] border-t-[var(--primary)]" />
      </div>
    );
  }

  if (isError || !session) {
    return <ApiErrorMessage error={error} onRetry={() => refetch()} />;
  }

  const isInterpreting = session.status === 'uploaded' || session.status === 'interpreting';
  // spec-aanmeldketen-sneller CAP-3: `submitting` is nog niet ingediend — het afronden loopt op de
  // achtergrond. Eigen staat met ordernummer en, zolang de API hem kent, een teller.
  const isSubmitting = session.status === 'submitting';
  const isSubmitted = session.status === 'submitted';

  return (
    <div data-testid="intake-detail-page">
      <Link className="mb-3 inline-block text-sm text-[var(--secondary)] hover:underline" to="/intake">
        {t('intake.detail.backToList')}
      </Link>
      {/* spec-klantsimulatie-aanmeldingen CAP-4: de vastlegging, alleen voor medewerkers. */}
      {session.vastlegging?.length ? (
        <section data-testid="intake-vastlegging" className="mb-5 text-sm text-[var(--foreground-muted)]">
          <h2 className="mb-1 font-semibold text-[var(--foreground-heading)]">{t('intake.detail.vastlegging.titel')}</h2>
          <ul>
            {session.vastlegging.map((regel, index) => (
              <li key={index}>
                {t(`intake.detail.vastlegging.${regel.handeling}`, {
                  rol: regel.rol,
                  medewerkerId: regel.medewerkerId,
                  tijdstip: new Date(regel.tijdstip).toLocaleString(i18n.language),
                })}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {session.status !== 'review' ? (
        <h1 className="mb-5 text-xl font-bold text-[var(--foreground-heading)]">{session.originalFilename}</h1>
      ) : null}

      {session.interrupted ? (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-4">
            <p role="alert" data-testid="intake-interrupted-message">{t('intake.detail.interrupted.message')}</p>
            <Button
              data-testid="intake-retry-button"
              disabled={retryMutation.isPending}
              onClick={() => retryMutation.mutate(session.id)}
            >
              {t('intake.detail.interrupted.retryCta')}
            </Button>
          </CardContent>
        </Card>
      ) : isInterpreting ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-4">
            <div className="h-6 w-6 shrink-0 animate-spin rounded-full border-4 border-[var(--border)] border-t-[var(--primary)]" />
            <p role="status" aria-live="polite" data-testid="intake-interpreting-message">
              {t(session.rowCount <= SYNC_ROW_THRESHOLD ? 'intake.detail.interpreting.syncMessage' : 'intake.detail.interpreting.asyncMessage')}
            </p>
          </CardContent>
        </Card>
      ) : session.status === 'review' ? (
        <IntakeReviewPage session={session} onPublishWarnings={setPublicatieWaarschuwingen} />
      ) : isSubmitting ? (
        <Card>
          <CardContent className="flex items-start gap-3 py-4">
            <div className="h-6 w-6 shrink-0 animate-spin rounded-full border-4 border-[var(--border)] border-t-[var(--primary)]" />
            <div className="min-w-0">
              <p role="status" aria-live="polite" className="break-words" data-testid="intake-submitting-message">
                {session.orderNumber
                  ? t('intake.detail.submitting.message', { orderNumber: session.orderNumber })
                  : t('intake.detail.submitting.messageNoOrder')}
              </p>
              {session.submitProgress ? (
                <p className="mt-1 text-sm text-[var(--foreground-muted)]" data-testid="intake-submitting-progress">
                  {t('intake.detail.submitting.progress', { handled: session.submitProgress.handled, total: session.submitProgress.total })}
                </p>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : isSubmitted ? (
        <Card>
          <CardContent className="py-4" data-testid="intake-submitted-message">
            <p role="status">{t('intake.detail.submitted.message')}</p>
            {publicatieWaarschuwingen.length > 0 ? (
              <ul data-testid="intake-submitted-warnings" className="mt-2 list-disc pl-5 text-sm">
                {publicatieWaarschuwingen.map((waarschuwing, index) => (
                  <li key={index}>{t(waarschuwing.key, { row: waarschuwing.row, value: waarschuwing.value })}</li>
                ))}
              </ul>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-4" data-testid="intake-expired-message">
            <p role="status">{t('intake.list.expiredNotice')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
